/**
 * Restores scroll position on PLP when returning from PDP.
 *
 * - On product card click: saves product handle, scroll position, loaded page count,
 *   and collection path to sessionStorage.
 * - On bfcache restore (pageshow): scrolls directly to saved position.
 * - On fresh page load: replays load-more fetches to restore products,
 *   then scrolls to the target card.
 */
(function () {
  const STORAGE_KEY = 'plp_scroll_restore';

  function getState() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function saveState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // sessionStorage full or unavailable
    }
  }

  function clearState() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  function getLoadedPageCount() {
    const btn = document.querySelector('[is="load-more-button"]');
    if (!btn) {
      // No button means all products are loaded or pagination is numbered.
      // Count from URL or assume we're on page 1 with all products visible.
      return 1;
    }
    const action = btn.getAttribute('action') || '';
    try {
      const url = new URL(action, window.location.origin);
      const nextPage = parseInt(url.searchParams.get('page'), 10);
      // The button points to the *next* page, so current loaded count = nextPage - 1
      return nextPage ? nextPage - 1 : 1;
    } catch {
      return 1;
    }
  }

  function scrollToProduct(handle) {
    const card = document.querySelector('[data-product-handle="' + CSS.escape(handle) + '"]');
    if (card) {
      card.scrollIntoView({ block: 'center', behavior: 'instant' });
      return true;
    }
    return false;
  }

  // --- Save state on product card click ---
  function initClickTracking() {
    // Use capture phase on document to guarantee we intercept before navigation,
    // regardless of stopPropagation calls or custom element boundaries.
    document.addEventListener('click', function (e) {
      const link = e.target.closest('a[href]');
      if (!link) return;

      const card = link.closest('[data-product-handle]');
      if (!card) return;

      // Only track clicks within the product grid
      if (!card.closest('#ProductsList')) return;

      const handle = card.getAttribute('data-product-handle');
      if (!handle) return;

      saveState({
        handle: handle,
        scrollY: window.scrollY,
        pageCount: getLoadedPageCount(),
        path: window.location.pathname + window.location.search,
      });
    }, true);
  }

  // --- Restore on bfcache ---
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;

    const state = getState();
    if (!state) return;

    if (state.path !== window.location.pathname + window.location.search) {
      clearState();
      return;
    }

    window.scrollTo(0, state.scrollY);
    clearState();
  });

  // --- Restore on fresh load ---
  async function restoreOnFreshLoad() {
    const state = getState();
    if (!state) return;

    if (state.path !== window.location.pathname + window.location.search) {
      clearState();
      return;
    }

    // If product already visible (page 1), just scroll
    if (scrollToProduct(state.handle)) {
      clearState();
      return;
    }

    // Need to replay load-more clicks
    if (state.pageCount > 1) {
      await replayLoadMore(state.pageCount);
    }

    // Try scrolling after products loaded
    requestAnimationFrame(function () {
      scrollToProduct(state.handle);
      clearState();
    });
  }

  function getSectionId() {
    if (typeof FoxTheme !== 'undefined' && FoxTheme.utils && FoxTheme.utils.getSectionId) {
      const container = document.getElementById('ProductGridContainer');
      if (container) return FoxTheme.utils.getSectionId(container);
    }
    const shopifySection = document.querySelector('#ProductGridContainer')?.closest('.shopify-section');
    if (shopifySection) return shopifySection.id.replace('shopify-section-', '');
    return null;
  }

  async function replayLoadMore(targetPageCount) {
    for (let i = 1; i < targetPageCount; i++) {
      const btn = document.querySelector('[is="load-more-button"]');
      if (!btn) break;

      const actionUrl = btn.getAttribute('action');
      if (!actionUrl) break;

      const url = new URL(actionUrl, window.location.origin);
      const sectionId = getSectionId();
      if (sectionId) url.searchParams.set('section_id', sectionId);

      try {
        const response = await fetch(url.toString());
        if (!response.ok) break;

        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');

        // Append products
        const productList = document.querySelector('#ProductGridContainer .products-list');
        const gridList = document.querySelector('#ProductGridContainer grid-list');
        const source = parsed.querySelector('.products-list');
        if (source && productList) {
          source.querySelectorAll('.card').forEach(function (item) {
            productList.appendChild(item);
          });
          if (gridList && gridList.reload) gridList.reload();
        }

        // Update pagination
        const pagination = document.querySelector('#ProductGridContainer .pagination');
        const newPagination = parsed.querySelector('.pagination');
        if (newPagination && pagination) {
          pagination.innerHTML = newPagination.innerHTML;
        } else if (!newPagination && pagination) {
          pagination.remove();
        }
      } catch {
        break;
      }
    }
  }

  // --- Init ---
  // Attach click tracking immediately (capture on document, no DOM dependency).
  initClickTracking();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreOnFreshLoad);
  } else {
    restoreOnFreshLoad();
  }
})();
